// /for-biotech — Emerging Biotech / Out-Licensors audience page.
// Anchored by Three Pillars: Partnering Intelligence · EU JCA / HTA · US AMCP / Payer-Value.
// "Look larger than life" — pharma-grade evidence at small-biotech price, MarksMan attribution.
// Featured anonymized cross-border case study.
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PublicNav from '@/components/PublicNav';
import PublicFooter from '@/components/PublicFooter';
import { useSEO } from '@/hooks/useSEO';
import { usePageTracker } from '@/hooks/usePageTracker';
import {
  ArrowRight, FlaskConical, Handshake, Globe2, Stethoscope, ExternalLink, X,
  CheckCircle2, Target, Microscope, Sparkles, Award
} from 'lucide-react';

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold });
    obs.observe(el); return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const PILLAR_CONTENT = {
  partnering: {
    icon: Handshake,
    accent: '#70C24C',
    title: 'Partnering Intelligence',
    subtitle: 'AI-scored outreach to the pharma buyers most likely to care',
    short: 'Curated intros to pre-qualified pharma buyers — never blasted.',
    body: 'ForesightRx maps your differentiated asset to the highest-fit pharma buyers and regional commercialization partners using AI-driven scoring against published deal patterns, therapeutic-area focus, and stated buyer criteria. Built on a database of 3,000+ biotech companies, 7,000+ assets, and 2,200+ mapped pharma BD contacts.',
    bullets: [
      'AI ranks pharma buyers by deal-pattern fit for each asset',
      'Bulk outreach with personalized intro emails and reply tracking',
      'Buyer-specific shortlists tuned to each pharma in-licensor\'s deck and BD thesis',
      'Conference attendee import (BIO International, ChinaBio, JPM)',
      'Web-grounded asset enrichment with citations (Perplexity Sonar)',
      'Public-facing status pages for transparency with your biotech',
    ],
  },
  eu_jca: {
    icon: Globe2,
    accent: '#00709B',
    title: 'EU JCA / HTA Readiness',
    subtitle: 'Joint Clinical Assessment — in scope since 12 January 2025 for oncology & ATMPs',
    short: 'Survive the EU JCA reviewer long before you sit across from one.',
    body: 'Under the EU HTA Regulation, every new oncology medicine and advanced therapy medicinal product undergoes a centralized Joint Clinical Assessment alongside EMA marketing authorization. JCA evaluates relative effectiveness, safety, comparators, and outcomes — and heavily influences national pricing and reimbursement decisions across all 27 EU member states. We prepare assets to meet this scrutiny during Phase 1 or 2 — when the cost of fixing the evidence base is still measured in months, not years.',
    bullets: [
      'EU JCA / HTA readiness assessment for oncology, ATMP, cell & gene therapy',
      'PICO and comparator landscape mapping aligned with EUnetHTA expectations',
      'Systematic literature reviews and structured evidence synthesis',
      'Indirect treatment comparison (ITC) feasibility and execution',
      'Clinical and payer evidence gap analysis',
      'Early payer value story development for EU diligence audiences',
      'Partner-facing value communication for licensing discussions',
    ],
  },
  us_payer: {
    icon: Stethoscope,
    accent: '#A88BFF',
    title: 'US AMCP / Payer-Value Readiness',
    subtitle: 'AMCP dossier · ICER preparedness · RWE strategy & economic modeling',
    short: 'There is no US equivalent of JCA — but AMCP, ICER, and PBMs make the same call.',
    body: 'There is no direct US equivalent of EU JCA, but AMCP-format dossiers, ICER value assessments, and PBM / MCO scrutiny are equally decisive for formulary access and reimbursement. We build the payer evidence package required for US commercial success — from health economic modeling to budget impact, real-world evidence planning, and payer objection mapping. Same rigour the top-20 pharma companies bring to their lead assets — applied to yours.',
    bullets: [
      'AMCP-format dossier strategy and authoring',
      'ICER-style value assessment preparedness',
      'Budget impact & cost-effectiveness modeling',
      'Real-world evidence (RWE) strategy and execution planning',
      'Payer objection mapping and response strategy',
      'Coverage pathway analysis for oncology, rare disease, ATMP',
      'Clinical-economic value communication for US payer audiences',
    ],
  },
};

const LARGER_THAN_LIFE = [
  { icon: <Microscope />, title: 'Pharma-grade evidence', desc: 'The same systematic reviews, ITCs, and budget-impact models we deliver to top-20 pharma — repackaged for your single asset. The reviewer can\'t tell the difference.' },
  { icon: <Target />, title: 'BD-ready positioning', desc: 'Problem → Patient → Proof framework. Non-confidential deck. Plain-language value story. Walks into any pharma BD meeting without flinching.' },
  { icon: <Award />, title: 'MarksMan Healthcare lineage', desc: '12 years of HEOR / HTA / market access work for global pharma. The same senior team. Just applied to your asset, not someone else\'s.' },
  { icon: <Handshake />, title: 'Pay only when you win', desc: 'Success-based partnering. No retainers. No subscriptions. We invest first; we share success only when your deal closes.' },
];

export default function ForBiotechPage() {
  useSEO({
    title: 'For Biotech — Look Larger Than Life to the Partners That Matter | ForesightRx',
    description: 'Emerging biotech out-licensing partner: AI-scored intros, EU JCA / HTA readiness, US AMCP / payer-value readiness. Pharma-grade evidence at small-biotech price. Pay only on success.',
    canonical: 'https://foresightrx.com/for-biotech',
  });
  usePageTracker('for-biotech');

  const [pillarOpen, setPillarOpen] = useState(null);
  const [heroRef, heroInView] = useInView(0);
  const [pillarRef, pillarInView] = useInView();
  const [largerRef, largerInView] = useInView();

  return (
    <div className="min-h-screen bg-[#0A1128] copy-protect" data-testid="for-biotech-page">
      <PublicNav />

      {/* HERO */}
      <section ref={heroRef} className="relative pt-32 lg:pt-40 pb-20 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 grid-bg" />
        <div className="absolute top-1/3 -right-32 w-[600px] h-[600px] rounded-full bg-[#70C24C]/15 blur-[120px] pulse-glow" />
        <div className="absolute bottom-0 -left-20 w-[400px] h-[400px] rounded-full bg-[#00709B]/10 blur-[100px]" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className={heroInView ? 'reveal-up' : 'opacity-0'}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#70C24C]/10 border border-[#70C24C]/30 mb-7">
              <FlaskConical className="w-3.5 h-3.5 text-[#70C24C]" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#70C24C]">For Emerging Biotech & Out-Licensors</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tighter leading-[1.05] max-w-4xl" data-testid="for-biotech-hero-heading">
              Look <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#70C24C] to-[#00709B]">larger than life</span>
              <br />
              to the partners that matter.
            </h1>
            <p className="mt-8 text-lg sm:text-xl text-[#A1A8C3] font-light max-w-3xl leading-relaxed">
              Great science is necessary. It is not sufficient. Pharma BD teams have three questions for every
              asset they look at: <strong className="text-white">Where is the patient?</strong>
              <strong className="text-white"> Where is the payer story?</strong>
              <strong className="text-white"> Where is the deal partner?</strong>
              We answer all three — with the same evidence rigour pharma applies internally, and a
              success-only fee structure that aligns us with your outcome.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/apply?intent=biotech_positioning"
                    className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#70C24C] text-[#0A1128] text-sm font-bold hover:bg-[#5fad3b] hover:-translate-y-0.5 transition-all duration-300 shadow-[0_0_30px_rgba(112,194,76,0.25)]"
                    data-testid="for-biotech-cta-review">
                Get a free positioning review <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/case-studies"
                    className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-white/15 text-white text-sm font-medium hover:bg-white/5 transition-all duration-300"
                    data-testid="for-biotech-cta-case-studies">
                See case studies
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* THREE PILLARS */}
      <section ref={pillarRef} className="py-24 lg:py-32 bg-[#0E1733]" data-testid="for-biotech-pillars">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className={`max-w-3xl mb-16 ${pillarInView ? 'reveal-up' : 'opacity-0'}`}>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#70C24C] mb-4">The Three Pillars</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
              Partnering. EU JCA readiness. US payer-value readiness.
            </h2>
            <p className="mt-5 text-lg text-[#A1A8C3] font-light">
              Three integrated capabilities. One asset. One outcome — a deal that closes, on terms that
              reflect what your science is actually worth.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-5 lg:gap-6">
            {Object.entries(PILLAR_CONTENT).map(([key, c], i) => {
              const Icon = c.icon;
              return (
                <button
                  key={key}
                  onClick={() => setPillarOpen(key)}
                  className={`group text-left bg-gradient-to-br from-[#111A3A] to-[#0A1128] border border-white/5 rounded-3xl p-7 lg:p-8 hover:border-[#70C24C]/40 hover:-translate-y-1 transition-all duration-500 ${pillarInView ? `reveal-up stagger-${i + 1}` : 'opacity-0'}`}
                  data-testid={`for-biotech-pillar-${key}`}
                >
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: `${c.accent}15`, color: c.accent }}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg lg:text-xl font-bold text-white mb-2">{c.title}</h3>
                  <p className="text-xs uppercase tracking-wider font-bold mb-3" style={{ color: c.accent }}>{c.subtitle}</p>
                  <p className="text-sm text-[#A1A8C3] font-light leading-relaxed mb-5">{c.short}</p>
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#70C24C] group-hover:translate-x-1 transition-transform duration-300">
                    See what we deliver <ArrowRight className="w-4 h-4" />
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* LARGER THAN LIFE */}
      <section ref={largerRef} className="py-24 lg:py-32 bg-[#0A1128]" data-testid="for-biotech-larger">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className={`max-w-3xl mb-16 ${largerInView ? 'reveal-up' : 'opacity-0'}`}>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#70C24C] mb-4">Larger than life</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
              Pharma-grade rigour. Small-biotech price.
            </h2>
            <p className="mt-5 text-lg text-[#A1A8C3] font-light">
              When pharma BD looks at your asset, they should see the same evidence package they
              themselves would commission. That's what we deliver — without the top-20 consulting fee.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {LARGER_THAN_LIFE.map((item, i) => (
              <div key={i}
                   className={`bg-[#111A3A] border border-white/5 rounded-2xl p-6 hover:border-[#70C24C]/30 transition-all ${largerInView ? `reveal-up stagger-${i + 1}` : 'opacity-0'}`}
                   data-testid={`for-biotech-larger-${i}`}>
                <div className="w-10 h-10 rounded-xl bg-[#70C24C]/10 flex items-center justify-center text-[#70C24C] mb-4 [&>svg]:w-5 [&>svg]:h-5">
                  {item.icon}
                </div>
                <h4 className="text-sm font-bold text-white mb-2">{item.title}</h4>
                <p className="text-xs text-[#A1A8C3] font-light leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className={`mt-10 bg-gradient-to-r from-[#70C24C]/8 to-[#00709B]/8 border border-white/5 rounded-2xl p-7 text-center ${largerInView ? 'reveal-up stagger-5' : 'opacity-0'}`}>
            <p className="text-sm sm:text-base text-[#A1A8C3] font-light leading-relaxed max-w-3xl mx-auto">
              Powered by <a href="https://marksmanhealthcare.com/" target="_blank" rel="noreferrer" className="text-[#70C24C] font-semibold hover:underline inline-flex items-center gap-1">MarksMan Healthcare <ExternalLink className="w-3 h-3" /></a>
              — 12 years of HEOR, HTA, market access, RWE and health economic modeling delivered to the
              world's largest pharma companies. The same senior team. Applied to your single asset.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 bg-[#111A3A] relative overflow-hidden" data-testid="for-biotech-cta">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-[#00709B]/10 to-[#70C24C]/10 blur-[100px]" />
        </div>
        <div className="relative max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight mb-6">
            The right pharma buyer is already in our network.
          </h2>
          <p className="text-lg text-[#A1A8C3] font-light mb-10 max-w-xl mx-auto">
            Send us a non-confidential one-pager. We will tell you — honestly — whether your asset
            has a partnerable story today, what is missing, and which pharma BD teams should see it first.
          </p>
          <Link to="/apply?intent=biotech_positioning"
                className="inline-flex items-center gap-2 px-10 py-4 rounded-full bg-[#70C24C] text-[#0A1128] text-base font-bold hover:bg-[#5fad3b] hover:-translate-y-0.5 transition-all duration-300 shadow-[0_0_30px_rgba(112,194,76,0.25)]"
                data-testid="for-biotech-bottom-cta">
            Get a free positioning review <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="mt-6 text-xs text-[#687399] uppercase tracking-wider">No upfront fees · Success-only partnering</p>
        </div>
      </section>

      <PublicFooter />

      {/* Pillar detail modal */}
      {pillarOpen && (
        <PillarModal id={pillarOpen} onClose={() => setPillarOpen(null)} />
      )}
    </div>
  );
}

function PillarModal({ id, onClose }) {
  const c = PILLAR_CONTENT[id];
  if (!c) return null;
  const Icon = c.icon;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid={`for-biotech-pillar-modal-${id}`}>
      <div
        className="bg-[#0E1733] border border-white/10 max-w-2xl w-full rounded-2xl shadow-2xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-7">
          <div className="flex items-start justify-between mb-5">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${c.accent}15`, color: c.accent }}>
                <Icon className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>{c.title}</h3>
                <p className="text-xs text-[#A1A8C3] mt-1">{c.subtitle}</p>
              </div>
            </div>
            <button onClick={onClose} className="text-[#A1A8C3] hover:text-white" data-testid={`for-biotech-pillar-close-${id}`}>
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-sm text-[#A1A8C3] leading-relaxed mb-5 font-light">{c.body}</p>
          <div className="text-[11px] font-bold text-[#70C24C] uppercase tracking-[0.2em] mb-3">What we deliver</div>
          <ul className="space-y-2 mb-6">
            {c.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-[#A1A8C3]">
                <CheckCircle2 className="w-3.5 h-3.5 mt-1 shrink-0" style={{ color: c.accent }} />
                <span>{b}</span>
              </li>
            ))}
          </ul>
          <div className="flex items-center justify-between gap-3 pt-5 border-t border-white/10">
            <a
              href="https://marksmanhealthcare.com/"
              target="_blank"
              rel="noreferrer"
              className="text-xs text-[#A1A8C3] hover:text-white inline-flex items-center gap-1"
            >
              MarksMan Healthcare — 12-year HEOR / HTA heritage
              <ExternalLink className="w-3 h-3" />
            </a>
            <Link
              to="/apply?intent=biotech_positioning"
              className="px-4 py-2 bg-[#70C24C] hover:bg-[#5fad3b] text-[#0A1128] text-xs font-bold rounded-full transition"
              data-testid={`for-biotech-pillar-cta-${id}`}
            >
              Talk to us →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
